'use client';

import clsx from 'clsx/lite';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Accordion } from '~/components/blocks/Accordion';
import { Woobox } from '~/components/blocks/Woobox';
import { Container } from '~/components/Container';
import { KlaviyoModal } from '~/components/KlaviyoModal';
import { Main } from '~/components/layouts/Main';
import { PageHero } from '~/components/PageHero';
import { ScheduleList } from '~/components/templates/Schedule/ScheduleList';
import { IGame } from '~/types/api';
import { TicketPackage, Venue } from '~/types/custom';
import type { AcfMediaItemConnectionEdge } from '~/types/graphql';
import { HeadlineSubheadCta } from '../HeadlineSubheadCta';
import { TicketPackageButton } from '../TicketPackageButton';
type TicketPackages = {
  disableSection?: boolean;
  heading?: string;
  ticketPackages?: TicketPackage[];
};

type OtherTicketPackage = {
  disableSection?: boolean;
  heading?: string;
  ticketPackages?: TicketPackage[];
};

export function VenueTemplate(props: { venue: Venue; schedules: any }) {
  const { venue, schedules } = props;
  const searchParams = useSearchParams();

  const {
    hero,
    localSponsor,
    ticketPackages,
    otherTicketPackages,
    faqs,
    scheduleHeading,
    postseasonScheduleHeading,
    allStarScheduleHeading,
    wooboxId,
  } = venue.venueContent;

  const { auslSchedule, sftblSchedule } = schedules;

  // group games by regular season and postseason
  const auslRegularSeasonGames =
    auslSchedule?.filter((game: any) => game.gameTypeLk === 'RS') || [];
  const auslPostseasonGames =
    auslSchedule?.filter((game: any) => game.gameTypeLk === 'PS') || [];
  const sftblRegularSeasonGames =
    sftblSchedule?.filter((game: any) => game.gameTypeLk === 'RS') || [];
  const sftblPostseasonGames =
    sftblSchedule?.filter((game: any) => game.gameTypeLk === 'PS') || [];

  return (
    <Main className={clsx('venue')}>
      <PageHero
        heading={hero.heading}
        subheading={hero.location}
        copy={hero.text}
        link={hero.ctaButton}
        image={hero?.photo as unknown as AcfMediaItemConnectionEdge}
        localSponsor={
          localSponsor as {
            label: string;
            sponsorLogo: AcfMediaItemConnectionEdge;
          }
        }
      />

      {/* TICKET PACKAGES */}
      {ticketPackages.ticketPackages?.length > 0 &&
        !ticketPackages.disableSection && (
          <div className="px-[--site-margin] py-16">
            <Container className="gap-y-9 lg:gap-y-8">
              <HeadlineSubheadCta headline={ticketPackages.heading} />
              <div className="relative flex flex-[0_0_auto] flex-col flex-wrap items-stretch justify-start gap-4 md:flex-row lg:gap-8">
                {ticketPackages.ticketPackages.map(
                  (ticketPackage: TicketPackage, index: number) => (
                    <div
                      key={index}
                      className={clsx(
                        'ticket-package flex min-h-full w-full flex-1 flex-col flex-wrap items-stretch justify-start rounded-md border py-12 lg:max-w-[50%]',
                        ticketPackages.ticketPackages.length > 2
                          ? 'px-8'
                          : 'px-16',
                      )}
                    >
                      {ticketPackage.title && (
                        <h3 className="mb-6 text-center font-nova text-[24px] font-black uppercase leading-[24px]">
                          {ticketPackage.title}
                        </h3>
                      )}
                      {ticketPackage.text && (
                        <div
                          className="rich-text"
                          dangerouslySetInnerHTML={{
                            __html: ticketPackage.text,
                          }}
                        />
                      )}
                      <div className="package-details mt-auto">
                        {ticketPackage.price && (
                          <p className="my-8 text-center font-nova text-[36px] font-black uppercase leading-[24px]">
                            {ticketPackage.price}
                          </p>
                        )}
                        {ticketPackage.purchaseLink?.url && (
                          <TicketPackageButton
                            url={ticketPackage.purchaseLink.url}
                            title={ticketPackage.purchaseLink.title}
                            target={ticketPackage.purchaseLink.target}
                          />
                        )}
                      </div>
                    </div>
                  ),
                )}
              </div>
            </Container>
          </div>
        )}

      {/* AUSL SCHEDULE */}
      {!!auslRegularSeasonGames.length && (
        <div className="px-[--site-margin] py-16">
          <Container className="gap-y-9 lg:gap-y-8">
            <HeadlineSubheadCta
              headline={scheduleHeading || 'Regular Season Games at this venue'}
              id="regular-season"
            />
            <ScheduleList
              games={auslRegularSeasonGames as unknown as IGame[]}
              className=""
              showLoadMore={true}
              initialGameCount={100}
              isAllStarCup={false}
            />
          </Container>
        </div>
      )}

      {!!auslPostseasonGames.length && (
        <div className="px-[--site-margin] py-16">
          <Container className="gap-y-9 lg:gap-y-8">
            <HeadlineSubheadCta
              headline={
                postseasonScheduleHeading || 'Postseason Games at this venue'
              }
              id="postseason"
            />
            <ScheduleList
              games={auslPostseasonGames as unknown as IGame[]}
              className=""
              showLoadMore={false}
              initialGameCount={100}
              isAllStarCup={false}
            />
          </Container>
        </div>
      )}

      {/* SFTBL SCHEDULE */}
      {!!sftblRegularSeasonGames.length && (
        <div className="px-[--site-margin] py-16">
          <Container className="gap-y-9 lg:gap-y-8">
            <HeadlineSubheadCta
              headline={
                allStarScheduleHeading || 'All-Star Cup Games at this venue'
              }
              id="regular-season"
            />
            <ScheduleList
              games={sftblRegularSeasonGames as unknown as IGame[]}
              className=""
              showLoadMore={true}
              initialGameCount={100}
              isAllStarCup={true}
            />
          </Container>
        </div>
      )}

      {/* OTHER TICKET PACKAGES */}
      {otherTicketPackages.ticketPackages?.length > 0 &&
        !otherTicketPackages.disableSection && (
          <div className="px-[--site-margin] py-16">
            <Container className="gap-y-9 lg:gap-y-8">
              <HeadlineSubheadCta headline={otherTicketPackages.heading} />
              <div className="relative inline-flex flex-[0_0_auto] flex-col flex-wrap items-center justify-start gap-4 md:flex-row lg:gap-8">
                {otherTicketPackages.ticketPackages.map(
                  (ticketPackage: TicketPackage, index: number) => (
                    <div key={index} className="flex-[0_1_25%]">
                      {ticketPackage.photo && (
                        <img
                          src={ticketPackage.photo.node?.sourceUrl}
                          alt={ticketPackage.photo.node?.altText}
                          className="w-full rounded-md"
                        />
                      )}
                      {ticketPackage.title && (
                        <h3 className="mb-6 mt-[24px] max-w-[90%] font-nova text-[20px] font-bold normal-case leading-[30px] lg:mt-[32px]">
                          {ticketPackage.title}
                        </h3>
                      )}
                      {ticketPackage.text && (
                        <div
                          className="rich-text [&>p]:text-[18px]"
                          dangerouslySetInnerHTML={{
                            __html: ticketPackage.text,
                          }}
                        />
                      )}
                      {ticketPackage?.purchaseLink?.url && (
                        <Link
                          href={ticketPackage?.purchaseLink?.url}
                          className="text-[14px] font-semibold uppercase tracking-[1px] underline underline-offset-4"
                        >
                          {ticketPackage?.purchaseLink?.title}
                        </Link>
                      )}
                    </div>
                  ),
                )}
              </div>
            </Container>
          </div>
        )}

      {/* FAQS */}
      {faqs && !faqs.disableSection && (
        <Container>
          <HeadlineSubheadCta headline={faqs.heading} />
          <Accordion
            accordion={faqs}
            isDynamic={false}
            isSmall={false}
            className="pb-16"
          />
        </Container>
      )}

      {wooboxId && (
        <div className="px-[--site-margin] py-16">
          <Woobox name="AcfWoobox" woobox={{ wooboxId }} />
        </div>
      )}

      <KlaviyoModal />
    </Main>
  );
}
